import './entitiesRisky.less'
import {
  ACTION_ENTITIES_WIDGET_SUMMARY,
  EntitiesSummaryWidget,
  EntitiesSummaryWidgetParams,
  fetchEntitiesWidgetSummary as fetchWidget,
  resetWidgetSummary as resetWidget
} from '../../../entities/entitiesSlice'
import { getUpdatedUrl } from '../../../../utils/urlUtil'
import {
  DATA_SOURCE_ID,
  DATA_SOURCE_TYPE,
  DATA_SOURCE_TYPES,
  FILTER_HAS_ENTITY_ACCESS,
  FILTER_RISK_KEY,
  PAGE_VIEW_KEY,
  PAGE_VIEW_VALUES,
  URL_ACCESS_CONTROL_USERS,
  URL_DASHBOARD,
  URL_ENTITIES,
  URL_USERS
} from '../../../../constants'
import { getShowLoader } from '../../../../reducers/requestReducer'
import { RootState } from '../../../../rootReducer'
import LoaderView from '../../../../components/Loader/loader'
import useUrlContext from '../../../../hooks/useUrlContext'
import NoEntitiesSvg from '../../../../assets/inlineSvg/no_entity.svg'
import ChartPie from '../../../../components/ChartPie/chartPie'
import { ChartColors, getUniqueWidgetIndex, UNIQUE_WIDGET_INDEX } from '../constants'
import ChartLabel from '../components/chartLabel'
import { LIGHT_THEME, useThemeContext } from '../components/ThemeContext'
import { ACTION_FETCH_ACCESS360_WIDGET } from '../../../accessControl/accessControlSlice'
import { mockEntitiesRiskyWidgetData } from '../../renderer/utils/widgetMockdata'
import useIsGovernanceEnabled from '../../../../configs/useIsGovernanceEnabled'
import { getDataSourceBasePath } from '../../../../utils/dataSourceUtil'
import { LabelItem } from '../../../accessControl/pages/dashboard/widgets/components/labelItem'
import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { Typography } from '@lightbeamai/design-system'

interface OwnProps {
  useMockData?: boolean
  parentUrl?: string
  isAnimated?: boolean
  chartPieWidth?: number
  widgetDatasourceId?: string
  widgetDashboardId?: string
  widgetDatasourceType?: DATA_SOURCE_TYPES
}

interface StateProps {
  widgetData?: EntitiesSummaryWidget
  usersCount?: number
  showLoader: boolean
}

interface DispatchProps {
  fetchWidget: (
    filters: EntitiesSummaryWidgetParams & {
      [UNIQUE_WIDGET_INDEX]?: string
      fetchUserWithAccessToEntitiesEnabled?: boolean
    }
  ) => void
  resetWidget: () => void
}

type EntitiesRiskyComponentProps = StateProps & DispatchProps & OwnProps

const EntitiesWidget = (props: EntitiesRiskyComponentProps) => {
  const {
    widgetData,
    usersCount = 0,
    showLoader,
    parentUrl,
    widgetDatasourceId,
    fetchWidget,
    resetWidget,
    isAnimated = true,
    chartPieWidth = 200,
    widgetDashboardId,
    widgetDatasourceType,
    useMockData = false
  } = props
  const { total = 0, totalRisky = 0 } = widgetData || {}

  const intl = useIntl()
  const context = useUrlContext()
  const datasourceId = widgetDatasourceId
    ? widgetDatasourceId
    : context.contextFilters[DATA_SOURCE_ID]
  const theme = useThemeContext().theme

  const isGovernanceEnabled = useIsGovernanceEnabled({
    context,
    datasourceId,
    datasourceType: widgetDatasourceType
  })

  useEffect(() => {
    if (useMockData) return
    fetchWidget({
      datasourceId,
      [UNIQUE_WIDGET_INDEX]: getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId),
      fetchUserWithAccessToEntitiesEnabled: isGovernanceEnabled
    })
    return () => resetWidget()
  }, [datasourceId, fetchWidget, resetWidget, useMockData, isGovernanceEnabled])

  const chartData = [
    {
      key: 'noRisk',
      value: total - totalRisky || 0,
      color: theme === LIGHT_THEME ? ChartColors.Purple : '#4E74FF'
    },
    {
      key: FILTER_RISK_KEY,
      value: totalRisky || 0,
      color: ChartColors.Orange
    }
  ]

  const pathBaseString = getDataSourceBasePath(
    widgetDatasourceType,
    datasourceId,
    context.pathname,
    false
  )

  const baseUrl = parentUrl ? parentUrl : pathBaseString

  const sumLink = getUpdatedUrl({
    pathName: baseUrl + URL_ENTITIES,
    pageName: URL_ENTITIES,
    pageQueryParams: {},
    queryParams: {
      ...(widgetDatasourceType && { [DATA_SOURCE_TYPE]: widgetDatasourceType })
    }
  })
  const noRiskLink = getUpdatedUrl({
    pathName: baseUrl + URL_ENTITIES,
    pageName: URL_ENTITIES,
    pageQueryParams: {
      [FILTER_RISK_KEY]: 'No risk'
    },
    queryParams: {
      ...(widgetDatasourceType && { [DATA_SOURCE_TYPE]: widgetDatasourceType })
    }
  })
  const riskLink = getUpdatedUrl({
    pathName: baseUrl + URL_ENTITIES,
    pageName: URL_ENTITIES,
    pageQueryParams: {
      [FILTER_RISK_KEY]: 'Risky'
    },
    queryParams: {
      ...(widgetDatasourceType && { [DATA_SOURCE_TYPE]: widgetDatasourceType })
    }
  })

  const accessBaseUrl =
    pathBaseString === URL_DASHBOARD
      ? URL_ACCESS_CONTROL_USERS
      : pathBaseString + URL_ACCESS_CONTROL_USERS

  const usersLink = getUpdatedUrl({
    pathName: accessBaseUrl,
    pageName: URL_USERS,
    pageQueryParams: {
      [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.card,
      [FILTER_HAS_ENTITY_ACCESS]: true
    },
    queryParams: {
      ...(widgetDatasourceType && { [DATA_SOURCE_TYPE]: widgetDatasourceType })
    }
  })

  return (
    <div
      className="entities-summary widget lb-widget-card xs-px-16 xs-pt-16 xs-pb-16"
      data-test-id="entities-entities-summary-widget"
    >
      <Typography color="primary" variant="h6">
        {intl.formatMessage({ id: 'entities.title' })}
      </Typography>
      <div className="summary-container">
        {widgetData && widgetData.total > 0 ? (
          <>
            <div className="charts-container">
              <ChartPie
                width={chartPieWidth}
                gap={5}
                data={chartData}
                link={[
                  { key: FILTER_RISK_KEY, link: riskLink },
                  { key: 'noRisk', link: noRiskLink }
                ]}
                text={intl.formatMessage({ id: 'entities.widgetRisk.total' })}
                centerCountStyles={{
                  fontSize: '2.1rem',
                  fontWeight: 700
                }}
                centerTextStyles={{
                  fontSize: '1rem',
                  fontWeight: 500
                }}
                totalLink={sumLink}
                isAnimated={isAnimated}
              />
              <div className="legend xs-mt-16">
                <ChartLabel
                  count={widgetData.total}
                  text={intl.formatMessage({ id: 'entities.widgetRisk.total' })}
                  lineColor={ChartColors.Purple}
                  onClick={() => window.open(sumLink)}
                />
                <ChartLabel
                  count={totalRisky}
                  text={intl.formatMessage({ id: 'entities.widgetRisk.atRisk' })}
                  lineColor={ChartColors.Orange}
                  onClick={() => window.open(riskLink)}
                />
              </div>
            </div>
            {isGovernanceEnabled && (
              <div className="accessible-users-count">
                <LabelItem
                  isApproximated
                  label={intl.formatMessage({ id: 'accessControl.access360.usersAccess' })}
                  count={usersCount}
                  link={usersLink}
                />
              </div>
            )}
          </>
        ) : (
          <div className="empty-state-icon ">
            <div className="icon-box">
              <NoEntitiesSvg />
            </div>

            <Typography color="placeholder" type="medium" variant="baseMinus2" className="xs-mt-16">
              {intl.formatMessage({ id: 'dashboard.widget.noEntityData' })}
            </Typography>
          </div>
        )}
      </div>

      <LoaderView showLoader={showLoader} size="small" />
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => ({
  widgetData: ownProps?.useMockData
    ? ((mockEntitiesRiskyWidgetData.widgetData as unknown) as StateProps['widgetData'])
    : state.entities.widgetSummaryData?.[
        getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
      ],
  usersCount: ownProps?.useMockData
    ? mockEntitiesRiskyWidgetData.usersCount
    : state.entities.widgetSummaryData?.[
        getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
      ]?.usersWithAccessToEntities,
  showLoader:
    getShowLoader(ACTION_ENTITIES_WIDGET_SUMMARY) || getShowLoader(ACTION_FETCH_ACCESS360_WIDGET)
})

const mapDispatchToProps: DispatchProps = { fetchWidget, resetWidget }

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(EntitiesWidget)
